import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Logout from '@mui/icons-material/Logout';
import {
  AppBar as BaseAppBar,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { FacilityState } from '@atom/FacilityState';
import { ReservationState } from '@atom/ReservationState';

type OwnProps = {
  facilityName?: string;
};

const AppBar = ({ facilityName }: OwnProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const resetReservation = useResetRecoilState(ReservationState);
  const facility = useRecoilValue(FacilityState);

  const toReturnHome = () => {
    resetReservation();
    navigate(`/c001/arealist?facilityId=${facility.facilityId}`);
    return undefined;
  };

  /* サイズチェック */
  const isMobileSize = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  let fontSize = '1rem';
  if (isMobileSize) {
    const checkFacilityName = facilityName || '';
    const size = checkFacilityName.length;
    if (size > 16) {
      fontSize = '0.6rem';
    } else if (size > 12) {
      fontSize = '0.8rem';
    }
  }

  /* 認証情報 */
  const { user, signOut } = useAuthenticator((context) => [context.user]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <BaseAppBar position="fixed" sx={{ bgcolor: '#02ada9' }}>
      <Toolbar variant="dense">
        <Typography variant="h6" sx={{ color: 'white !important', flexGrow: 1, fontSize }}>
          <span
            role="button"
            tabIndex={0}
          >
            {facilityName}
          </span>
        </Typography>
        <Button 
          variant="outlined" 
          color="white" 
          sx={{
            borderRadius: '15px',
            fontSize: '10px', 
            marginRight: '15px'
          }}
          onClick={(event) => {
            event.stopPropagation();
            toReturnHome();
          }}
        >
          TOPへ戻る
        </Button>
        {user ? (
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleClick}
              size="small"
              sx={{ ml: 2 }}
              color="white"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <AccountCircleOutlinedIcon sx={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="white"
            sx={{
              borderRadius: '15px',
              fontSize: '10px'
            }}
            onClick={() => {
              // ログインページを表示する。その際に、元ページを表示できるように現在のpathnameを渡す。
              navigate('/c003/login', {
                state: { nextPathName: `${location.pathname}${location.search}` },
                replace: true
              });
            }}
          >
            ログイン・新規登録
          </Button>
        )}
      </Toolbar>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <AccountCircleOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {user && user.username}
        </MenuItem>
        <Divider />
        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          ログアウト
        </MenuItem>
      </Menu>
    </BaseAppBar>
  );
};

AppBar.defaultProps = {
  facilityName: '指定なし'
};

export default AppBar;
